// hud-view.ts — the in-game HUD BOTTOM BAR DOM overlay (HTML/DOM UI shell plan,
// Phase 5, Form 5). This is the FIRST DOM element that PERSISTS over the LIVE
// canvas playfield (the modals — objectives / pause menu / summary / options —
// were transient full-screen overlays). It establishes the INPUT-SPLIT pattern for
// the rest of the gameplay UI.
//
// It renders the canvas InputUIForm bottom bar (inputui.ts kidfInputUI 1037):
//   - MENU button bottom-LEFT (kidcMenuButton) → deps.onMenu() (the host wires it to
//     the inGameMenu seam → the DOM pause menu — the SAME seam the canvas MENU
//     button uses, inputui.ts:307 InGameMenu).
//   - CREDITS LEFT of center (CreditsControl) — a framed cyan BOX with the $ icon +
//     the right-aligned credit count, rendered through the REAL kifntHud font (the
//     glyphs 'ABC'/'LBC' + the number, exactly as CreditsControl.OnPaint draws them).
//     Tappable → deps.onCreditsTap() (showObjectives(ksoObjectives), the SAME seam
//     the canvas credits tap uses, inputui.ts:321-332).
//   - POWER RIGHT of center (PowerControl) — a framed cyan BOX 'DEF' + the power
//     symbol 'G' + the 3-state meter pips ('I' green / 'J' yellow / 'K' red) + the
//     demand marker 'H', ALSO rendered through the REAL kifntHud font. Display-only.
// Both readouts are now FONT-RENDERED IMAGES (the kifntHud glyphs A–L are ICONS /
// frame-pieces, not letters — a CSS approximation can't match them), produced by
// hud-readout.ts (renderCredits / renderPower reproduce CreditsControl.OnPaint /
// PowerControl.OnPaint) and shown as <img> sized to the glyph extent × the design
// scale. CREDITS + POWER straddle the screen midpoint as a CENTERED block (cxMargin
// half-gap, the canvas inputui.ts:208-210 layout), with MENU at the far left.
// The numbers come from the SAME getters the canvas reads (plrLocal.GetCredits /
// GetNeedCreditsCount / GetPowerDemand / GetPowerSupply) — pushed per-frame via
// update(); DISPLAY-only, no sim commands. The images re-render only on change.
//
// INPUT SPLIT (the key design): the overlay CONTAINER is pointer-events:none so map
// gestures fall THROUGH to the canvas (which keeps handling select/drag/scroll/place
// via injectPen); ONLY the interactive elements (the MENU button + the credits
// image) are pointer-events:auto. Power is display. The bar is pinned to the screen
// bottom; the rest of the window shows the canvas. The canvas InputUIForm display
// controls are Show(false)'d while the DOM HUD is active so the bar does not
// double-render (the canvas still owns the playfield + its keyboard map-scroll).
//
// Chrome (per the design preference): the MENU button LABEL + the CREDITS NUMBER use
// the WEB (non-bitmap) font — like the rest of the DOM UI — at a SMALLER size than the
// shell buttons, NOT the canvas's baked bitmap (menuup.tbm) / bitmap-font number. The
// FRAMES + ICONS stay bitmap (the credits box + $ icon, the power box + meter + pips),
// since those are art, not text. So: MENU = the 9-slice button frame + a small web
// "MENU"; CREDITS = the bitmap box/$ icon + a small web-font number overlaid
// right-aligned in the 'AB' span; POWER is fully bitmap (no number).

import { titleArtToDataUrl, composeButtonDataUrl, type ButtonArt } from '../title-art.ts';
import { ksfxGuiButtonTap } from '../../ui/uikit.ts';
import { el } from '../chrome.ts';
import type { Font } from '../../gfx/font.ts';
import { type HudState, kCreditsDisplayMax } from './hud-format.ts';
import { renderCredits, renderPower } from './hud-readout.ts';

export interface HudViewDeps {
  /** the overlay container (created by the host; this view owns its contents). It is
   *  the pointer-events:none input-split container — set by this view's ctor. */
  root: HTMLElement;
  /** the MENU button → the inGameMenu seam (the host routes it to the DOM pause
   *  menu, the SAME seam the canvas MENU button uses). When ABSENT the button is not
   *  created — a dead MENU button reads as broken (the editor playtest exits via its
   *  own Stop control, so it omits this). */
  onMenu?: () => void;
  /** the credits-readout tap → showObjectives(ksoObjectives) (the host routes it to
   *  the DOM objectives briefing, the SAME seam the canvas credits tap uses). */
  onCreditsTap: () => void;
  /** the power-readout TAP-HOLD (a ~500ms press without sliding off) → toggle the
   *  debug/test-options panel. A hidden developer gesture: the power meter is otherwise
   *  display-only, so a deliberate hold there is a safe place to surface the panel. When
   *  absent the power readout stays purely display (pointer-events fall through). */
  onPowerTapHold?: () => void;
  /** TS-only: the ATTACK-MOVE toggle button → arm/disarm attack-move on the SimUI. When
   *  absent the button is not created (the editor playtest host can omit it). */
  onAttackMoveToggle?: () => void;
  /** apuntc[kutReactor].nPowerSupply — the power supplied per Reactor = the
   *  units-per-pip the canvas PowerControl uses (inputui.ts:692). Constant per
   *  level; read once at mount. */
  powerSupplyPerPip: number;
  /** the REAL kifntHud bitmap font the canvas CreditsControl/PowerControl draw
   *  through (its glyphs A–L are the box/icon/pip art). The HUD renders the readouts
   *  into bitmaps with it so they are PIXEL-FAITHFUL to the canvas. */
  hudFont: Font;
  /** the UI chrome magnification (main.ts kUiMagnify ≈ the design scale / 2). The bar's
   *  game-px art (the kifntHud glyphs, the MENU button) is drawn at native resolution × this.
   *  It used to be kGameScale (matching the canvas HUD pixel size), but the canvas HUD is
   *  Show(false)'d under the DOM bar, so it now follows the decoupled UI scale — bigger than
   *  the map on phones so the bar + tap targets stay legible while the map keeps its scale. */
  magnify: number;
  /** the canvas console (input) strip height in GAME pixels (cyInput = menuButton.cy
   *  + 2*4). The DOM bar height = this × magnify so it matches the canvas strip. */
  consoleStripPx: number;
  /** the 9-slice button pack art — the SAME composited frame the shell menu / pause
   *  menu buttons use. Used by the MENU button ONLY as a fallback when menuArt is absent
   *  (then: this frame + a WEB-font "MENU" label). Absent ⇒ a flat CSS text-button. */
  buttonArt?: ButtonArt;
  /** the REAL menu-button bitmaps (menuup.png / menudown.png — data-URLs). The canvas
   *  ButtonControl::OnPaint blits these DIRECTLY at the kidcMenuButton rect (InputUI.cpp
   *  via misccontrols.cpp:155-167) — they are the canvas's LIGHTER single-line rounded
   *  border with "MENU" baked in (NOT the heavier beveled 9-slice the shell uses). The HUD
   *  uses them so the in-game MENU button is pixel-faithful to the canvas. Absent ⇒ the
   *  9-slice + web-label fallback above. */
  menuArt?: { up: string; down: string; cx: number; cy: number };
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

// OPAQUE (was 0.78): the bar is drawn at the CHROME magnify (kUiMagnify), which on phones is
// taller than the canvas's own input strip (kGameScale), so the bar's upper band overlays LIVE
// map terrain. A translucent bg let that terrain bleed through, reading as a couple-px "gap"
// between the map and the solid HUD. Opaque hides it cleanly — the map ends flush at the bar's
// top edge. (Over the canvas's black input strip below, opaque vs 0.78 looks identical anyway.)
const COL_BAR_BG = 'rgb(8, 14, 16)';
const COL_SEP = '#8fd0e0'; // the bar's top separator (SP cyan side color)
const FONT = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';

// the canvas bar margins in GAME pixels (inputui.ts:207-208): cxMargin=6 (the
// straddle half-gap — credits' right edge at width/2 - cxMargin, power's left edge at
// width/2 + cxMargin), cyMargin=4 (the MENU button top; readouts at cyMargin+1).
const CX_MARGIN = 6;
const CY_MARGIN = 4;

// the power-meter tap-hold gesture (opens the debug panel): the press must be held this
// long without sliding off, mirroring the canvas penHoldEvent (event.ts kctPenHoldDelay
// = 50 ticks ≈ 500ms). HOLD_SLOP_PX is the move tolerance in CSS px (the canvas cancels
// on >7 GAME px; this is the scaled-up DOM equivalent — generous so a steady finger holds).
const POWER_HOLD_MS = 500;
const POWER_HOLD_SLOP_PX = 12;

export class HudView {
  private readonly d: HudViewDeps;
  /** the bottom bar element (the only visible child of the input-split container). */
  private bar!: HTMLElement;
  private menuBtn!: HTMLButtonElement;
  /** the CREDITS readout — a button wrapping the bitmap box <img> + the WEB-font number
   *  overlaid (the box/$ icon are art ⇒ bitmap; the number is web font per the design). */
  private creditsEl!: HTMLButtonElement;
  private creditsImg!: HTMLImageElement;
  private creditsNumEl!: HTMLElement;
  /** extent('AB') in native px — where the web number right-aligns (before the 'C' cap). */
  private creditsNumRight = 0;
  /** the POWER readout — the font-rendered <img> (display-only). */
  private powerEl!: HTMLElement;
  private powerImg!: HTMLImageElement;
  /** TS-only: the ATTACK-MOVE toggle button (created only when onAttackMoveToggle is wired). */
  private attackBtn?: HTMLButtonElement;
  /** the native (font-px) extents of each readout image — the bitmap is this size;
   *  the <img> is scaled to extent × the design scale at layout time. */
  private creditsCx = 0;
  private creditsCy = 0;
  private powerCx = 0;
  private powerCy = 0;
  /** the last state pushed — update() is a no-op when nothing changed (cheap). */
  private last: HudState | null = null;
  /** cache the rendered data URLs by their EXACT inputs so we don't re-render every
   *  frame (only when credits/symbol or supply/demand/demandMax/symbol change). */
  private creditsUrlCache = new Map<string, string>();
  private powerUrlCache = new Map<string, string>();

  constructor(d: HudViewDeps) {
    this.d = d;
    // THE INPUT SPLIT: the container is a full-window overlay at pointer-events:none
    // so EVERY gesture over it (the map) passes THROUGH to the canvas underneath.
    // Only the interactive bar elements opt back into pointer-events:auto (below).
    const s = d.root.style;
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '10'; // above the canvas, BELOW the modal overlays (z=20)
    s.pointerEvents = 'none'; // ← the split: map gestures fall through to canvas
    s.userSelect = 'none';
    s.fontFamily = FONT;
    this.build();
    window.addEventListener('resize', this.onResize);
  }

  private readonly onResize = (): void => {
    this.layout();
  };

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.powerHoldTimer !== null) {
      window.clearTimeout(this.powerHoldTimer);
      this.powerHoldTimer = null;
    }
    this.d.root.replaceChildren();
    this.last = null;
  }

  // ── the canvas magnification (kGameScale): the canvas draws the bar's game-px art
  // at native resolution and presents the whole back buffer scaled by this, so the
  // DOM bar applies the SAME factor → its game-px readouts/button land at the
  // IDENTICAL CSS size as the canvas HUD. ──────────────────────────────────────────
  private unit(): number {
    return this.d.magnify;
  }

  private build(): void {
    // the bottom bar — pinned to the screen bottom, full width, the ONLY visible
    // child of the input-split container. pointer-events:none on the bar itself so
    // a tap on the bar's empty middle still falls through; the MENU button + credits
    // image re-enable pointer-events. position:relative establishes the containing
    // block for the absolutely-positioned readouts (the canvas straddle layout, below).
    const bar = el('div', {
      position: 'absolute',
      left: '0',
      right: '0',
      bottom: '0',
      background: COL_BAR_BG,
      borderTop: `1px solid ${COL_SEP}`,
      pointerEvents: 'none',
      boxSizing: 'border-box',
    });
    this.bar = bar;

    // MENU button — bottom LEFT (kidcMenuButton at (cxMargin, cyMargin), inputui.ts:210).
    // It's the canvas MENU button's OWN bitmap art (menuup/menudown.tbm — "MENU" baked
    // in), NOT the 9-slice. pointer-events:auto (interactive). Far left, absolute.
    this.menuBtn = this.menuButton(() => this.d.onMenu?.());
    this.menuBtn.style.position = 'absolute';
    this.menuBtn.style.pointerEvents = 'auto';
    this.menuBtn.setAttribute('data-hud', 'menu');
    // no in-game-menu seam wired (the editor playtest) → hide the button entirely; a
    // visible MENU that does nothing reads as broken. Layout still sizes it (harmless).
    if (this.d.onMenu === undefined) this.menuBtn.style.display = 'none';

    // CREDITS — LEFT of center (inputui.ts:211: its RIGHT edge at width/2 - cxMargin).
    // A button (keyboard/ARIA + the tap routing) wrapping the font-rendered <img>.
    // pointer-events:auto. Right-anchored so its right edge lands cxMargin left of
    // the midpoint — the canvas straddle. Top at cyMargin+1 game px (inputui.ts:212).
    this.creditsEl = el('button', {
      position: 'absolute',
      pointerEvents: 'auto',
      cursor: 'pointer',
      border: 'none',
      background: 'transparent',
      padding: '0',
      display: 'block',
      lineHeight: '0',
    }) as HTMLButtonElement;
    this.creditsEl.setAttribute('data-hud', 'credits');
    this.creditsImg = el('img', {
      display: 'block',
      imageRendering: 'pixelated',
    }) as HTMLImageElement;
    this.creditsImg.setAttribute('data-hud-credits-img', '');
    this.creditsImg.alt = 'credits';
    // the WEB-font credit number, overlaid right-aligned in the box's 'AB' span (the
    // box/$ icon are the bitmap; only the NUMBER is the web font, per the design pref).
    this.creditsNumEl = el('div', {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      fontFamily: FONT,
      fontWeight: '700',
      color: '#e8f4f8',
      textShadow: '0 1px 2px rgba(0,0,0,0.9)',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      overflow: 'hidden',
    });
    this.creditsNumEl.setAttribute('data-hud-credits-num', '');
    this.creditsEl.append(this.creditsImg, this.creditsNumEl);
    this.creditsEl.addEventListener('pointerdown', () => {
      this.d.playSfx?.(ksfxGuiButtonTap);
    });
    this.creditsEl.addEventListener('click', () => this.d.onCreditsTap());

    // POWER — RIGHT of center (inputui.ts:213: its LEFT edge at width/2 + cxMargin).
    // DISPLAY-only by default (pointer-events:none — map gestures over it fall through);
    // when onPowerTapHold is wired it opts INTO pointer-events:auto so it can catch the
    // tap-hold (a deliberate ~500ms press) that toggles the debug panel.
    // Left-anchored so its left edge lands cxMargin right of the midpoint.
    const powerInteractive = this.d.onPowerTapHold !== undefined;
    this.powerEl = el('div', {
      position: 'absolute',
      pointerEvents: powerInteractive ? 'auto' : 'none',
      lineHeight: '0',
      // when the tap-hold is wired, suppress the native long-press behaviors the
      // gesture would otherwise trigger on touch: iOS's image callout (Share / Save
      // to Photos / Copy), text selection, and the touch being treated as a scroll.
      ...(powerInteractive
        ? {
            webkitTouchCallout: 'none',
            userSelect: 'none',
            touchAction: 'none',
          }
        : {}),
    });
    this.powerEl.setAttribute('data-hud', 'power');
    this.powerImg = el('img', {
      display: 'block',
      imageRendering: 'pixelated',
      // the long-press callout fires on the <img> itself; kill it here too (and the
      // drag-to-save) so a hold over the meter never surfaces the browser's image menu.
      ...(powerInteractive ? { webkitTouchCallout: 'none', userSelect: 'none' } : {}),
    }) as HTMLImageElement;
    this.powerImg.setAttribute('data-hud-power-img', '');
    this.powerImg.alt = 'power';
    if (powerInteractive) this.powerImg.draggable = false;
    this.powerEl.append(this.powerImg);
    if (this.d.onPowerTapHold !== undefined) this.wirePowerTapHold();

    // ATTACK-MOVE toggle — bottom RIGHT (TS-only). A square glyph button that arms the
    // attack-move mode; the next ground tap issues an attack-move and disarms it. Its active
    // state is driven by HudState.attackMoveArmed in update(). Only created when wired.
    if (this.d.onAttackMoveToggle !== undefined) {
      const b = el('button', {
        position: 'absolute',
        pointerEvents: 'auto',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        borderRadius: '4px',
        boxSizing: 'border-box',
        border: `1px solid ${COL_SEP}`,
        background: 'rgba(20,32,36,0.85)',
        color: '#cfe8f0',
        fontFamily: FONT,
        fontWeight: '700',
        lineHeight: '1',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
        userSelect: 'none',
        touchAction: 'manipulation',
      }) as HTMLButtonElement;
      b.setAttribute('data-hud', 'attackmove');
      b.title = 'Attack-move (tap, then tap ground)';
      b.setAttribute('aria-label', 'Attack-move');
      b.textContent = '⌖'; // ⌖ position/crosshair glyph
      b.addEventListener('pointerdown', () => {
        this.d.playSfx?.(ksfxGuiButtonTap);
      });
      b.addEventListener('click', () => this.d.onAttackMoveToggle?.());
      this.attackBtn = b;
    }

    bar.append(this.menuBtn, this.creditsEl, this.powerEl);
    if (this.attackBtn !== undefined) bar.append(this.attackBtn);
    this.d.root.replaceChildren(bar);
    this.layout();
    // render the initial (zero) state so the readouts have content before the first
    // per-frame update() push.
    this.last = null;
    this.update({
      credits: 0,
      needCredits: 0,
      powerDemand: 0,
      powerSupply: 0,
      demandMax: 0,
      flashOn: true,
    });
  }

  /** the power-meter TAP-HOLD → onPowerTapHold (toggles the debug panel). A pure DOM
   *  re-creation of the canvas penHoldEvent (event.ts): a pointerdown starts a timer; if
   *  the finger lifts (pointerup), slides off (>HOLD_SLOP_PX), or cancels before it fires,
   *  the hold is aborted; otherwise after POWER_HOLD_MS the callback runs. A plain tap
   *  does nothing — only a deliberate hold surfaces the panel. */
  private powerHoldTimer: number | null = null;
  private wirePowerTapHold(): void {
    const cancel = (): void => {
      if (this.powerHoldTimer !== null) {
        window.clearTimeout(this.powerHoldTimer);
        this.powerHoldTimer = null;
      }
    };
    let x0 = 0,
      y0 = 0;
    this.powerEl.addEventListener('pointerdown', (e: PointerEvent) => {
      cancel();
      x0 = e.clientX;
      y0 = e.clientY;
      this.powerHoldTimer = window.setTimeout(() => {
        this.powerHoldTimer = null;
        this.d.onPowerTapHold?.();
      }, POWER_HOLD_MS);
    });
    this.powerEl.addEventListener('pointermove', (e: PointerEvent) => {
      // cancel the hold once the finger slides past the slop (the canvas's >7px abort).
      if (
        this.powerHoldTimer !== null &&
        (Math.abs(e.clientX - x0) > POWER_HOLD_SLOP_PX ||
          Math.abs(e.clientY - y0) > POWER_HOLD_SLOP_PX)
      ) {
        cancel();
      }
    });
    this.powerEl.addEventListener('pointerup', cancel);
    this.powerEl.addEventListener('pointerleave', cancel);
    this.powerEl.addEventListener('pointercancel', cancel);
    // a hold IS our gesture, not a request for the browser context / image menu —
    // swallow contextmenu so the native menu never appears over the panel.
    this.powerEl.addEventListener('contextmenu', (e: Event) => {
      e.preventDefault();
    });
  }

  // size the bar + its controls to the canvas geometry × the magnification. The
  // canvas InputUIForm lays the bar out in GAME pixels (SizeAndPositionEverything,
  // inputui.ts:178-213): the console strip is cyInput tall (menuButton.cy + 2*cyMargin),
  // the MENU button is at (cxMargin, cyMargin), and the credits/power readouts sit at
  // cyMargin+1 straddling the midpoint (credits' RIGHT edge at width/2 - cxMargin,
  // power's LEFT edge at width/2 + cxMargin). The whole back buffer is then presented
  // scaled by kGameScale — so the DOM bar applies the SAME factor to the SAME game-px
  // metrics and lands at the IDENTICAL CSS size as the canvas HUD.
  private layout(): void {
    const u = this.unit(); // kGameScale
    const stripPx = this.d.consoleStripPx; // cyInput (game px)
    const barH = Math.round(stripPx * u);
    const padX = Math.round(CX_MARGIN * u); // cxMargin (=6 game px)
    const cyMargin = Math.round(CY_MARGIN * u); // cyMargin (=4 game px)
    this.bar.style.height = `${barH}px`;
    // the canvas straddle (inputui.ts:211-213): credits' right edge at the midpoint
    // minus cxMargin, power's left edge at the midpoint plus cxMargin (a CENTERED
    // block, NOT space-between). The half-gap is cxMargin × the magnification.
    const gapHalf = `${padX}px`;
    // MENU button — at (cxMargin, cyMargin); it's the menuup.tbm bitmap drawn at its
    // NATIVE size × the magnification — the IDENTICAL CSS size as the canvas MENU
    // button (the canvas ButtonControl sizes its rect to the bitmap, misccontrols.ts:
    // 297-305, and the back buffer is presented scaled by kGameScale). The bitmap
    // height = stripPx - 2*cyMargin (cyInput = menuButton.cy + 2*cyMargin), so the
    // native height fallback agrees with the strip geometry when the art is absent.
    // the MENU button. When the real menuup.png bitmap is present, the canvas blits it at
    // its NATIVE size into the kidcMenuButton rect (ButtonControl::OnPaint BltTo, no stretch
    // — misccontrols.cpp:159), so the faithful CSS size is the bitmap extent × magnify (a
    // bit WIDER than the old 2.6×height heuristic, and the LIGHTER single-line border the
    // bitmap carries). Fallback: the canvas button area (height = cyInput − 2*cyMargin =
    // menuButton.cy game px) at a compact MENU aspect × magnify.
    const btnAreaCy = stripPx - 2 * CY_MARGIN;
    let mbW: number, mbH: number;
    if (this.d.menuArt !== undefined) {
      mbW = Math.round(this.d.menuArt.cx * u);
      mbH = Math.round(this.d.menuArt.cy * u);
    } else {
      mbH = Math.round(btnAreaCy * u);
      mbW = Math.round(2.6 * btnAreaCy * u);
    }
    this.menuBtn.style.left = `${padX}px`;
    this.menuBtn.style.top = `${cyMargin}px`;
    this.menuBtn.style.width = `${mbW}px`;
    this.menuBtn.style.height = `${mbH}px`;
    if (this.d.menuArt !== undefined) {
      // the real bitmap IS the background (MENU baked in) — no web label, no 9-slice.
      this.menuBtn.style.backgroundImage = `url(${this.d.menuArt.up})`;
    } else {
      // the in-game MENU label (web font) — bumped up to ~the shell button ratio (h*0.5).
      this.menuBtn.style.fontSize = `${Math.max(8, Math.round(mbH * 0.5))}px`;
      // composite the 9-slice frame to the button's source-px aspect, then show the up state.
      if (this.d.buttonArt !== undefined) {
        this.menuArtW = Math.max(1, Math.round((mbW / mbH) * this.d.buttonArt.midUp.cy));
        this.setMenuDown(false);
      }
    }
    // CREDITS — right-anchored so its RIGHT edge sits cxMargin LEFT of the midpoint;
    // top at cyMargin+1 game px (inputui.ts:212).
    this.creditsEl.style.right = `calc(50% + ${gapHalf})`;
    this.creditsEl.style.top = `${Math.round((CY_MARGIN + 1) * u)}px`;
    // POWER — left-anchored so its LEFT edge sits cxMargin RIGHT of the midpoint.
    this.powerEl.style.left = `calc(50% + ${gapHalf})`;
    this.powerEl.style.top = `${Math.round((CY_MARGIN + 1) * u)}px`;
    // ATTACK-MOVE toggle — bottom RIGHT, a square the height of the MENU button area.
    if (this.attackBtn !== undefined) {
      const sz = Math.round(btnAreaCy * u);
      this.attackBtn.style.right = `${padX}px`;
      this.attackBtn.style.top = `${cyMargin}px`;
      this.attackBtn.style.width = `${sz}px`;
      this.attackBtn.style.height = `${sz}px`;
      this.attackBtn.style.fontSize = `${Math.max(10, Math.round(sz * 0.6))}px`;
    }
    // the readout IMAGES are scaled from their native font extent by the SAME
    // magnification the canvas presents at (uniform — the kifntHud glyphs keep their
    // aspect, no distortion, pixel-faithful to the canvas).
    this.scaleReadouts(u);
  }

  /** scale the credits/power <img> to their native font extent × the magnification
   *  (the SAME factor the canvas presents the game-px glyphs at). */
  private scaleReadouts(u: number): void {
    if (this.creditsCy > 0) {
      this.creditsImg.style.height = `${Math.round(this.creditsCy * u)}px`;
      this.creditsImg.style.width = `${Math.round(this.creditsCx * u)}px`;
      // the WEB number: right edge at extent('AB') × u (before the 'C' cap), vertically
      // centered, a SMALLER font than the shell (≈0.7 of the box height — fits the 'B'
      // number area). text-shadow + flex-end give the canvas's right-aligned look.
      this.creditsNumEl.style.right = `${Math.round((this.creditsCx - this.creditsNumRight) * u)}px`;
      this.creditsNumEl.style.fontSize = `${Math.max(8, Math.round(this.creditsCy * u * 0.7))}px`;
      this.creditsNumEl.style.paddingBottom = `${Math.round(u)}px`; // optical centering vs the box
    }
    if (this.powerCy > 0) {
      this.powerImg.style.height = `${Math.round(this.powerCy * u)}px`;
      this.powerImg.style.width = `${Math.round(this.powerCx * u)}px`;
    }
  }

  /** the per-frame push from the canvas InputUIForm.Update() seam — re-render only
   *  on change (cheap). DISPLAY-only. */
  update(state: HudState): void {
    const p = this.last;
    if (
      p !== null &&
      p.credits === state.credits &&
      p.needCredits === state.needCredits &&
      p.powerDemand === state.powerDemand &&
      p.powerSupply === state.powerSupply &&
      p.demandMax === state.demandMax &&
      p.flashOn === state.flashOn &&
      !!p.attackMoveArmed === !!state.attackMoveArmed
    ) {
      return; // nothing changed
    }
    this.last = { ...state };

    // ATTACK-MOVE toggle active state — armed reads red/highlighted, idle reads dim.
    if (this.attackBtn !== undefined) {
      const armed = state.attackMoveArmed === true;
      this.attackBtn.style.background = armed ? 'rgba(200,32,32,0.92)' : 'rgba(20,32,36,0.85)';
      this.attackBtn.style.borderColor = armed ? '#ff6b6b' : COL_SEP;
      this.attackBtn.style.color = armed ? '#fff' : '#cfe8f0';
      this.attackBtn.setAttribute('aria-pressed', armed ? 'true' : 'false');
    }

    // CREDITS — CreditsControl.OnPaint (inputui.ts:551-568): the box draws 'ABC' with
    // the $ icon, or 'LBC' when the symbol is flashed off. The canvas flashes the
    // credit SYMBOL while structures need credits (cCreditNeeders>0, inputui.ts:576);
    // when the perf flash option is off, flashOn is forced true upstream ⇒ 'ABC' stays.
    const lowCredits = state.needCredits > 0;
    const drawSymbol = !lowCredits || state.flashOn;
    // cache by the clamped count (the renderer clamps to kCreditsDisplayMax, so all
    // counts past the clamp share one rendered image) + the symbol-flash phase.
    const clampedCredits = Math.min(state.credits, kCreditsDisplayMax);
    // the BOX + $ icon only (drawNumber=false) — the number is the web font, overlaid.
    // Cache by the symbol-flash phase (the box image is value-independent).
    const cKey = `box:${drawSymbol ? 1 : 0}`;
    let cUrl = this.creditsUrlCache.get(cKey);
    if (cUrl === undefined || this.creditsCx === 0) {
      const img = renderCredits(this.d.hudFont, 0, drawSymbol, false);
      this.creditsCx = img.cx;
      this.creditsCy = img.cy;
      this.creditsNumRight = img.numRightPx ?? img.cx;
      cUrl = titleArtToDataUrl(img);
      this.creditsUrlCache.set(cKey, cUrl);
    }
    this.creditsImg.src = cUrl;
    // the WEB-font number (right-aligned in 'AB' via scaleReadouts).
    this.creditsNumEl.textContent = String(clampedCredits);

    // POWER — PowerControl.OnPaint (inputui.ts:657-709): the box 'DEF', the power
    // symbol 'G' (shown unless flashed off while low), the 3-state pips, the demand
    // marker. m_fShowPowerSymbol flashes when supply<demand (m_fPowerLow), gated on
    // the perf flash option (flashOn forced true upstream when off ⇒ symbol stays).
    const lowPower = state.powerDemand > state.powerSupply;
    const showPowerSymbol = !lowPower || state.flashOn;
    const pKey = `${state.powerDemand}:${state.powerSupply}:${state.demandMax}:${showPowerSymbol ? 1 : 0}`;
    let pUrl = this.powerUrlCache.get(pKey);
    if (pUrl === undefined) {
      const img = renderPower(
        this.d.hudFont,
        state.powerDemand,
        state.powerSupply,
        this.d.powerSupplyPerPip,
        state.demandMax,
        showPowerSymbol,
      );
      this.powerCx = img.cx;
      this.powerCy = img.cy;
      pUrl = titleArtToDataUrl(img);
      this.powerUrlCache.set(pKey, pUrl);
    }
    this.powerImg.src = pUrl;

    // size the (possibly newly-extent'd) images to the current scale.
    this.scaleReadouts(this.unit());
  }

  // ── the MENU button. PREFERRED: the canvas's REAL menuup.png / menudown.png bitmap
  // (ButtonControl::OnPaint blits it directly — misccontrols.cpp:155-167) — the LIGHTER
  // single-line rounded border with "MENU" baked in, sized to the bitmap extent × magnify
  // in layout() (wider than the old heuristic). FALLBACK (no menuArt): the 9-slice button
  // FRAME (shell/pause pack art) + a small web-font "MENU" label, or a flat CSS button when
  // even the 9-slice art is absent. ───────────────────────────────────────────────────────
  private setMenuDown: (down: boolean) => void = () => {};
  private readonly menuArtCache = new Map<string, string>();
  private menuButton(onClick: () => void): HTMLButtonElement {
    const tap = (): void => {
      this.d.playSfx?.(ksfxGuiButtonTap);
    };
    const hasMenuArt = this.d.menuArt !== undefined;
    const has9Slice = this.d.buttonArt !== undefined;
    // the real bitmap / 9-slice both go in backgroundImage (transparent button); only the
    // bare flat-CSS fallback fills COL_SEP + shows the web label.
    const hasArt = hasMenuArt || has9Slice;
    const b = el('button', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: hasArt ? 'transparent' : COL_SEP,
      padding: '0',
      borderRadius: hasArt ? '0' : '3px',
      cursor: 'pointer',
      fontFamily: FONT,
      fontWeight: '700',
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
      color: hasArt ? '#fff' : '#0b1316',
      textShadow: hasArt ? '0 1px 2px rgba(0,0,0,0.7)' : 'none',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: '100% 100%',
    }) as HTMLButtonElement;
    // the real bitmap carries "MENU"; only the 9-slice / flat fallback overlays a web label.
    b.textContent = hasMenuArt ? '' : 'MENU';
    // layout() sets the up bitmap / composites the 9-slice; setMenuDown swaps up/down.
    this.setMenuDown = (down: boolean): void => {
      if (this.d.menuArt !== undefined) {
        b.style.backgroundImage = `url(${down ? this.d.menuArt.down : this.d.menuArt.up})`;
        return;
      }
      if (this.d.buttonArt === undefined || this.menuArtW <= 0) return;
      b.style.backgroundImage = `url(${this.menuComposite(this.menuArtW, down)})`;
    };
    b.addEventListener('pointerdown', () => {
      this.setMenuDown(true);
      tap();
    });
    const rel = (): void => this.setMenuDown(false);
    b.addEventListener('pointerup', rel);
    b.addEventListener('pointerleave', rel);
    b.addEventListener('pointercancel', rel);
    b.addEventListener('click', onClick);
    return b;
  }

  /** the composited-9-slice art width in source px (from the button's CSS aspect) — set
   *  in layout(); the composited bitmap is cached by (width:down). */
  private menuArtW = 0;
  private menuComposite(wArt: number, down: boolean): string {
    const key = `${wArt}:${down ? 1 : 0}`;
    let u = this.menuArtCache.get(key);
    if (u === undefined) {
      u = composeButtonDataUrl(this.d.buttonArt!, wArt, down);
      this.menuArtCache.set(key, u);
    }
    return u;
  }
}
